import type { LevelDefinition } from './Level.ts';

/**
 * Level 8 — the pipe.
 *
 * Every level so far has been solvable by pulling. This one is not: the fluid starts
 * sealed in a tube whose closed end faces the goal, so an attractor on the goal pulls the
 * blob straight into that end wall and holds it there. The run settles in three seconds
 * having never left the pipe. It is the first level where the obvious move does not
 * merely go to the wrong place — it does nothing at all.
 *
 * A magnet works through a wall; fluid does not. So the answer is a negative magnet on the
 * far side of the tube's closed end, firing the blob out of the muzzle. From there the
 * baffle takes it down the right-hand side and the collector reels it back under the pipe.
 *
 * The bucket under the muzzle is not decoration and was checked: without it, five more
 * launcher placements succeed. It is there to punish a stream that leaves nose-down.
 *
 * Proven headlessly: the intended pair solves in 7.1s, an attractor on the goal settles at
 * 0% at (312, 532) — still inside the tube — a second attractor placed to "lead it out"
 * just collects the blob onto itself, and no single magnet solves it from any of 270 grid
 * positions.
 */
export const level08: LevelDefinition = {
  id: 'level08',
  name: 'The pipe',
  hint: 'A magnet works through a wall. The fluid does not.',
  magnetsAvailable: 2,
  fluid: {
    amount: 380,
    source: { x: 250, y: 500 },
    radius: 44,
  },
  goal: { x: 230, y: 1280, radius: 115, type: 'fluid', fraction: 0.45 },
  walls: [
    // Arena.
    { type: 'segment', x1: 120, y1: 240, x2: 880, y2: 240, thickness: 26 },
    { type: 'segment', x1: 880, y1: 240, x2: 880, y2: 1400, thickness: 26 },
    { type: 'segment', x1: 880, y1: 1400, x2: 120, y2: 1400, thickness: 26 },
    { type: 'segment', x1: 120, y1: 1400, x2: 120, y2: 240, thickness: 26 },

    // The tube: closed against the left wall, mouth on the right.
    { type: 'segment', x1: 120, y1: 380, x2: 560, y2: 380, thickness: 26 },
    { type: 'segment', x1: 120, y1: 620, x2: 560, y2: 620, thickness: 26 },

    // The baffle that forces the descent down the right, and the bucket under the muzzle
    // that catches anything leaving nose-down.
    { type: 'segment', x1: 560, y1: 620, x2: 560, y2: 1160, thickness: 26 },
    { type: 'segment', x1: 560, y1: 1060, x2: 700, y2: 1060, thickness: 26 },
    { type: 'segment', x1: 700, y1: 760, x2: 700, y2: 1060, thickness: 26 },
  ],
};
